import { apiFetch, apiRequest } from "./client";
import type { Account, LoginResult } from "./types";
export interface RegisterResult {
  account: Account;
  enrollment_required: boolean;
  enroll_ticket: string | null;
}
interface EnrollStart {
  email_masked: string | null;
  expires_at: string;
}
interface EnrollStatus {
  email_masked: string | null;
  email_verified: boolean;
  email_pending: boolean;
  mfa_method: "email" | "totp" | null;
}
interface EnrollMethodResult {
  enrolled: boolean;
  recovery_codes?: string[] | null;
  otpauth_uri?: string | null;
  secret?: string | null;
}
const post = (body: unknown) => ({
  method: "POST",
  body: JSON.stringify(body),
  skipRefresh: true,
});
export const auth = {
  me: () => apiFetch<Account>("/auth/me"),
  mePoll: () =>
    apiFetch<Account>("/auth/me", { skipRefresh: true }).catch(() => null),
  login: (username: string, password: string) =>
    apiFetch<LoginResult>("/auth/login", post({ username, password })),
  loginMfa: (body: {
    mfa_ticket: string;
    code: string;
    remember_device: boolean;
  }) => apiFetch<Account>("/auth/login/mfa", post(body)),
  logout: () => apiFetch<void>("/auth/logout", { method: "POST" }),
  registerByInvite: (body: {
    invite_token: string;
    username: string;
    password: string;
    email?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  }) => apiFetch<RegisterResult>("/auth/register/invite", post(body)),
  inviteMeta: (token: string) =>
    apiFetch<{
      email_required: boolean;
      authority_required?: boolean;
      legacy_review_required?: boolean;
    }>(`/auth/register/invite/${encodeURIComponent(token)}/meta`, {
      skipRefresh: true,
    }),
  enrollEmailStart: (body: { enroll_ticket: string; email?: string | null }) =>
    apiFetch<EnrollStart>("/auth/enroll/email/start", post(body)),
  enrollEmailConfirm: (body: { enroll_ticket: string; code: string }) =>
    apiFetch<{ email_verified: boolean }>(
      "/auth/enroll/email/confirm",
      post(body),
    ),
  enrollStatus: (ticket: string) =>
    apiFetch<EnrollStatus>(
      "/auth/enroll/status?enroll_ticket=" + encodeURIComponent(ticket),
      { skipRefresh: true },
    ),
  enrollMfaSelect: (body: {
    enroll_ticket: string;
    method: "email" | "totp";
    remember_device: boolean;
  }) => apiFetch<EnrollMethodResult>("/auth/enroll/mfa/select", post(body)),
  enrollTotpVerify: (body: {
    enroll_ticket: string;
    code: string;
    remember_device: boolean;
  }) =>
    apiFetch<{ enrolled: boolean; recovery_codes: string[] }>(
      "/auth/enroll/mfa/totp/verify",
      post(body),
    ),
  recoveryPdf: async (codes: string[]): Promise<Blob> =>
    (
      await apiRequest("/auth/me/2fa/recovery/pdf", {
        method: "POST",
        body: JSON.stringify({ codes }),
      })
    ).blob(),
};
